import { Box, Checkbox, Button, Divider, FormControl, InputBase, InputLabel, ListItemText, ListSubheader, MenuItem, OutlinedInput, Select, Grid, CircularProgress } from '@mui/material';
import { DateRangePicker, SingleInputDateRangeField } from '@mui/x-date-pickers-pro';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { useTranslation } from 'react-i18next';
import { getBorder } from 'utils/Styles';
import { Context as ScheduleContext } from '../table/schedule/Context';
import { Context as EmployeeContext } from '../table/employee/Context';
import { Context as TimecardContext } from '../table/timecard/Context';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { shortcuts } from 'utils/Times';
import SearchIcon from '@mui/icons-material/Search';
import { gettingEmployees } from 'utils/Filter';
import moment from 'moment';

const Context = {
    schedule: ScheduleContext,
    employee: EmployeeContext,
    timecard: TimecardContext,
};

type FilterView = 'schedule' | 'employee' | 'timecard';

interface FilterProps {
    view: FilterView;
}

interface Tag {
    id: number | string;
    name: string;
}

interface Employee {
    id: number | string;
    full_name: string;
    tag: string;
}

const Filter = ({ view }: FilterProps) => {
    const { t } = useTranslation();

    const {
        setRows,
        tableDateRange,
        setTableDateRange,
        tags,
        tag,
        setTag,
        filterTags,
        setFilterTags,
        loadingGettingTags,
        employees,
        setEmployees,
        employee,
        setEmployee,
        filterEmployees,
        setFilterEmployees,
        loadingGettingEmployees,
        setLoadingGettingEmployees,
        reloading,
    } = useContext(Context[view] as any) as any;

    const [firtsRender, setFirstRender] = useState(true);

    useEffect(() => {
        if (firtsRender && employee.length !== 0) {
            if (view !== 'employee') {
                reloading();
            }
            setFirstRender(false);
        }
    }, [firtsRender, employee, reloading, view]);

    const tagsFilterLc = filterTags.toLowerCase();
    const employeesFilterLc = filterEmployees.toLowerCase();

    const visibleTags = useMemo<Tag[]>(() => (tags ?? []).filter((tg: Tag) => tg.name.toLowerCase().includes(tagsFilterLc)), [tags, tagsFilterLc]);

    const selectedTagNames = useMemo(() => new Set<string>(tag.map((t: Tag) => t.name)), [tag]);

    const tagsAllChecked = useMemo(() => {
        if (!tags || tags.length === 0) return false;
        if (filterTags === '') {
            return tags.filter((tg: Tag) => selectedTagNames.has(tg.name)).length === tags.length;
        }
        return visibleTags.length > 0 && visibleTags.every((tg) => selectedTagNames.has(tg.name));
    }, [tags, visibleTags, selectedTagNames, filterTags]);

    const tagsIndeterminate = useMemo(() => {
        if (!tags || tags.length === 0) return false;
        if (filterTags === '') {
            const selectedCount = tags.filter((tg: Tag) => selectedTagNames.has(tg.name)).length;
            return selectedCount !== tags.length && tag.length > 0;
        }
        if (visibleTags.length === 0) return false;
        const allIn = visibleTags.every((tg) => selectedTagNames.has(tg.name));
        const noneIn = visibleTags.every((tg) => !selectedTagNames.has(tg.name));
        return !allIn && !noneIn;
    }, [tags, visibleTags, selectedTagNames, filterTags, tag.length]);

    const visibleEmployees = useMemo<Employee[]>(() => (employees ?? []).filter((e: Employee) => e.full_name.toLowerCase().includes(employeesFilterLc)), [employees, employeesFilterLc]);

    const selectedEmployeeNames = useMemo(() => new Set<string>(employee.map((e: Employee) => e.full_name)), [employee]);

    const employeesAllChecked = useMemo(() => {
        if (!employees || employees.length === 0) return false;
        if (filterEmployees === '') {
            return employees.filter((e: Employee) => selectedEmployeeNames.has(e.full_name)).length === employees.length;
        }
        return visibleEmployees.length > 0 && visibleEmployees.every((e) => selectedEmployeeNames.has(e.full_name));
    }, [employees, visibleEmployees, selectedEmployeeNames, filterEmployees]);

    const employeesIndeterminate = useMemo(() => {
        if (!employees || employees.length === 0) return false;
        if (filterEmployees === '') {
            const selectedCount = employees.filter((e: Employee) => selectedEmployeeNames.has(e.full_name)).length;
            return selectedCount !== employees.length && employee.length > 0;
        }
        if (visibleEmployees.length === 0) return false;
        const allIn = visibleEmployees.every((e) => selectedEmployeeNames.has(e.full_name));
        const noneIn = visibleEmployees.every((e) => !selectedEmployeeNames.has(e.full_name));
        return !allIn && !noneIn;
    }, [employees, visibleEmployees, selectedEmployeeNames, filterEmployees, employee.length]);

    // Refetch usando SOLO los tags realmente seleccionados (payload previamente enviaba todos los tags).
    const refetchEmployeesByTags = useCallback(
        (selectedTags: Tag[]) => {
            gettingEmployees(
                setLoadingGettingEmployees,
                setEmployees,
                setEmployee,
                setRows,
                selectedTags.map((tg) => tg.id),
                view,
                {
                    tags_id_in: JSON.stringify(selectedTags.map((tg) => tg.id)),
                    start_date: tableDateRange[0].format('YYYY-MM-DD'),
                    end_date: tableDateRange[1].format('YYYY-MM-DD'),
                    without_hiring: view === 'employee' ? true : false,
                },
            );
        },
        [setLoadingGettingEmployees, setEmployees, setEmployee, setRows, tableDateRange, view],
    );

    const handleTagsGlobalToggle = useCallback(
        (checked: boolean) => {
            let nextTag: Tag[];
            if (checked) {
                nextTag = filterTags === '' ? tags : visibleTags;
            } else {
                nextTag = filterTags === '' ? [] : tag.filter((t: Tag) => !visibleTags.some((vt) => vt.name === t.name));
            }
            setTag(nextTag);
            refetchEmployeesByTags(nextTag);
        },
        [tags, visibleTags, tag, setTag, filterTags, refetchEmployeesByTags],
    );

    const handleTagItemClick = useCallback(
        (tg: Tag) => {
            const nextTag = selectedTagNames.has(tg.name) ? tag.filter((t: Tag) => t.name !== tg.name) : [...tag, tg];
            setTag(nextTag);
            refetchEmployeesByTags(nextTag);
        },
        [tag, setTag, selectedTagNames, refetchEmployeesByTags],
    );

    const handleEmployeesGlobalToggle = useCallback(
        (checked: boolean) => {
            if (checked) {
                setEmployee(filterEmployees === '' ? employees : visibleEmployees);
            } else {
                setEmployee(filterEmployees === '' ? [] : employee.filter((e: Employee) => !visibleEmployees.some((ve) => ve.full_name === e.full_name)));
            }
        },
        [employees, visibleEmployees, employee, setEmployee, filterEmployees],
    );

    const handleEmployeeItemClick = useCallback(
        (e: Employee) => {
            setEmployee(selectedEmployeeNames.has(e.full_name) ? employee.filter((emp: Employee) => emp.full_name !== e.full_name) : [...employee, e]);
        },
        [employee, setEmployee, selectedEmployeeNames],
    );

    return (
        <Box
            width={{ sm: '80%', md: '90%', lg: '100%' }}
            sx={{
                flexDirection: 'row',
                justifyContent: 'flex-start',
                alignItems: 'center',
                gap: 2,
                p: 1.5,
                borderBottom: (theme) => getBorder(theme.palette.mode),
            }}
        >
            <Grid container spacing={2} direction={'row'}>
                <Grid size={{ sm: 2, md: 3, lg: 3 }}>
                    <LocalizationProvider dateAdapter={AdapterMoment} adapterLocale={moment.locale()}>
                        <DateRangePicker
                            label={t('utils.filter.range_of_days')}
                            slots={{ field: SingleInputDateRangeField }}
                            formatDensity="spacious"
                            slotProps={{
                                shortcuts: { items: shortcuts(t) },
                                textField: {
                                    size: 'small',
                                    sx: {
                                        '& .MuiInputBase-root': { cursor: 'pointer' },
                                        '& .MuiInputBase-input': { cursor: 'pointer' },
                                        '& .MuiIconButton-root': { cursor: 'pointer' },
                                        width: '100%',
                                    },
                                    fullWidth: true,
                                },
                                field: { dateSeparator: ' a ' },
                            }}
                            calendars={1}
                            onChange={(value) => setTableDateRange(value)}
                            value={tableDateRange}
                        />
                    </LocalizationProvider>
                </Grid>

                <Grid size={{ sm: 3, md: 3, lg: 3 }}>
                    <FormControl fullWidth required error={tag.length === 0 && !loadingGettingTags} size="small">
                        <InputLabel id="lsg">{t('utils.filter.tags')}</InputLabel>
                        <Select
                            labelId="lsg"
                            id="sg"
                            multiple
                            disabled={loadingGettingTags}
                            value={tag.map((t: Tag) => t.name)}
                            input={<OutlinedInput label={t('utils.filter.tags')} />}
                            renderValue={(selected) => (selected as string[]).join(', ')}
                            MenuProps={{ style: { maxHeight: '400px' } }}
                        >
                            {loadingGettingTags && (
                                <MenuItem disabled sx={{ justifyContent: 'center', gap: 1 }}>
                                    <CircularProgress size={20} />
                                    <ListItemText primary={t('utils.filter.loading')} />
                                </MenuItem>
                            )}

                            {!loadingGettingTags && tags?.length !== 0 && (
                                <Box>
                                    <Checkbox sx={{ ml: 2 }} checked={tagsAllChecked} indeterminate={tagsIndeterminate} onChange={(e) => handleTagsGlobalToggle(e.target.checked)} />

                                    <InputBase
                                        sx={{ width: '70%', mx: 1.5, my: 1.5 }}
                                        placeholder={t('utils.filter.search')}
                                        endAdornment={<SearchIcon sx={{ mr: 1 }} />}
                                        value={filterTags}
                                        onChange={(e) => setFilterTags(e.target.value)}
                                        onKeyDown={(e) => e.stopPropagation()}
                                    />

                                    <Divider />

                                    {visibleTags.map((tg) => (
                                        <MenuItem key={tg.id} onClick={() => handleTagItemClick(tg)}>
                                            <Checkbox checked={selectedTagNames.has(tg.name)} />
                                            <ListItemText primary={tg.name} />
                                        </MenuItem>
                                    ))}
                                </Box>
                            )}
                        </Select>
                    </FormControl>
                </Grid>

                <Grid size={{ sm: 4, md: 5, lg: 5 }}>
                    <FormControl fullWidth error={employee.length === 0 && !loadingGettingEmployees} size="small">
                        <InputLabel id="lem">{t('utils.filter.employees')}</InputLabel>
                        <Select
                            labelId="lem"
                            id="em"
                            multiple
                            disabled={loadingGettingEmployees}
                            value={employee.map((e: Employee) => e.full_name)}
                            input={<OutlinedInput label={t('utils.filter.employees')} />}
                            renderValue={(selected) => (selected as string[]).join(', ')}
                            MenuProps={{ style: { maxHeight: '400px' } }}
                        >
                            {loadingGettingEmployees && (
                                <MenuItem disabled sx={{ justifyContent: 'center', gap: 1 }}>
                                    <CircularProgress size={20} />
                                    <ListItemText primary={t('utils.filter.loading')} />
                                </MenuItem>
                            )}

                            {!loadingGettingEmployees && employees.length !== 0 && (
                                <Box>
                                    <Checkbox sx={{ ml: 2 }} checked={employeesAllChecked} indeterminate={employeesIndeterminate} onChange={(e) => handleEmployeesGlobalToggle(e.target.checked)} />

                                    <InputBase
                                        sx={{ width: 'calc(100% - 80px)', ml: 1, mb: 1.5 }}
                                        placeholder={t('utils.filter.search')}
                                        endAdornment={<SearchIcon sx={{ mr: 1 }} />}
                                        value={filterEmployees}
                                        onChange={(e) => setFilterEmployees(e.target.value)}
                                        onKeyDown={(e) => e.stopPropagation()}
                                    />

                                    <Divider />

                                    {tags
                                        .filter((tg: Tag) => selectedTagNames.has(tg.name))
                                        .map((tg: Tag) => (
                                            <Box key={tg.id}>
                                                <ListSubheader>{tg.name}</ListSubheader>
                                                {visibleEmployees
                                                    .filter((e) => e.tag === tg.name)
                                                    .map((e) => (
                                                        <MenuItem key={`${e.id}-${e.tag}`} value={e.full_name} onClick={() => handleEmployeeItemClick(e)}>
                                                            <Checkbox checked={selectedEmployeeNames.has(e.full_name)} />
                                                            <ListItemText primary={e.full_name} />
                                                        </MenuItem>
                                                    ))}
                                            </Box>
                                        ))}
                                </Box>
                            )}
                        </Select>
                    </FormControl>
                </Grid>

                <Grid size={{ sm: 1, md: 1, lg: 1 }}>
                    <Button
                        fullWidth
                        loading={loadingGettingEmployees}
                        variant="contained"
                        disabled={tableDateRange[0] === null || tableDateRange[1] === null || tag.length === 0 || employee.length === 0}
                        onClick={() => reloading()}
                    >
                        {t('utils.filter.search')}
                    </Button>
                </Grid>
            </Grid>
        </Box>
    );
};

export { Filter };
